<div class="min-h-screen bg-[color:var(--background)] pt-24 pb-20 text-[color:var(--foreground)]">
  <div class="mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-10">

    <div class="mb-12 text-center">
      <span class="inline-flex items-center gap-2 rounded-full border border-[color:var(--border)] bg-[color:var(--card)] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[color:var(--muted-foreground)]">
        <lucide-icon name="help-circle" class="h-3.5 w-3.5 text-[color:var(--primary)]"></lucide-icon>
        Help Center
      </span>
      <h1 class="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Frequently Asked Questions</h1>
      <p class="mt-4 text-lg sm:text-xl text-[color:var(--muted-foreground)]">Find answers to common questions about using nibni.</p>

      <div class="mt-8 mx-auto max-w-2xl relative">
        <lucide-icon name="search" class="h-5 w-5 text-[color:var(--muted-foreground)] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"></lucide-icon>
        <input
          [(ngModel)]="searchQuery"
          (ngModelChange)="onSearchInput()"
          placeholder="Search FAQs..."
          class="w-full rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] pl-12 pr-4 py-4 text-base text-[color:var(--foreground)] placeholder:text-[color:var(--muted-foreground)] shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]/50 focus:border-[color:var(--ring)]"
        />
      </div>
    </div>

    <div *ngIf="loading" class="py-20 text-center text-[color:var(--muted-foreground)]">
      <lucide-icon name="loader-2" class="h-8 w-8 mx-auto animate-spin text-[color:var(--primary)]"></lucide-icon>
      <p class="mt-3">Loading FAQs...</p>
    </div>

    <div *ngIf="!loading && error" class="rounded-2xl border border-red-200 bg-red-50 px-6 py-10 text-center text-red-600">
      {{ error }}
    </div>

    <ng-container *ngIf="!loading && !error">
      <div *ngIf="!groups.length" class="rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] px-6 py-16 text-center text-[color:var(--muted-foreground)] shadow-sm">
        <lucide-icon name="help-circle" class="h-10 w-10 mx-auto text-[color:var(--muted-foreground)] opacity-50"></lucide-icon>
        <p class="mt-3">{{ searchQuery ? 'No FAQs match your search.' : 'No FAQs available yet.' }}</p>
      </div>

      <div class="space-y-6">
        <div *ngFor="let group of groups" [attr.id]="'faq-category-' + group.slug" class="rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] shadow-[0_8px_30px_rgba(15,23,42,0.06)] overflow-hidden">

          <div class="flex items-center gap-4 px-5 sm:px-7 py-5 border-b border-[color:var(--border)] cursor-pointer select-none transition-colors hover:bg-[color:var(--muted)]"
               (click)="toggleGroupExpanded(group)">
            <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
                 [style.background]="group.bgColor">
              <lucide-icon [name]="group.icon" class="h-5 w-5" [style.color]="group.color"></lucide-icon>
            </div>
            <div class="flex-1 min-w-0">
              <h2 class="text-lg font-semibold text-[color:var(--foreground)] truncate">{{ group.category }}</h2>
              <p class="text-sm text-[color:var(--muted-foreground)]">{{ group.items.length }} question{{ group.items.length !== 1 ? 's' : '' }}</p>
            </div>
            <button type="button" (click)="shareCategory(group); $event.stopPropagation()"
                    class="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border)] bg-[color:var(--card)] px-3 py-1.5 text-xs font-semibold text-[color:var(--muted-foreground)] shadow-sm transition hover:bg-[color:var(--muted)] hover:text-[color:var(--foreground)]"
                    [title]="'Share ' + group.category + ' FAQs'">
              <lucide-icon [name]="group.shareCopied ? 'check' : 'share-2'" class="h-3.5 w-3.5" [class.text-emerald-600]="group.shareCopied"></lucide-icon>
              {{ group.shareCopied ? 'Copied!' : 'Share' }}
            </button>
            <span class="inline-flex items-center justify-center h-7 min-w-[28px] rounded-full px-2.5 text-xs font-bold text-white"
                  [style.background]="group.color">
              {{ group.items.length }}
            </span>
            <lucide-icon name="chevron-down" class="h-5 w-5 text-[color:var(--muted-foreground)] transition-transform duration-200"
                         [class.rotate-180]="group.expanded"></lucide-icon>
          </div>

          <div *ngIf="group.expanded" class="divide-y divide-[color:var(--border)]">
            <div *ngFor="let faq of getVisibleItems(group); trackBy: trackFaq" class="faq-accordion-item">
              <details class="group">
                <summary class="cursor-pointer px-5 sm:px-7 py-5 flex items-start justify-between gap-6 text-left text-base sm:text-[1.0625rem] font-semibold leading-relaxed text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--muted)] group-open:text-[color:var(--primary)]">
                  <span class="faq-question">{{ faq.question }}</span>
                  <span class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center text-2xl leading-none text-[color:var(--primary)] transition-transform duration-200 group-open:rotate-45">+</span>
                </summary>
                <div class="px-5 sm:px-7 pb-7 pt-1 faq-answer overflow-hidden" [innerHTML]="faq.answer"></div>
              </details>
            </div>

            <div *ngIf="hasMore(group)" class="px-5 sm:px-7 py-4 bg-[color:var(--muted)]">
              <button (click)="viewAll(group); $event.stopPropagation()"
                      class="text-sm font-semibold transition-colors hover:underline"
                      [style.color]="group.color">
                View all {{ group.items.length }} questions &rarr;
              </button>
            </div>
          </div>
        </div>
      </div>
    </ng-container>

    <div class="mt-14 rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] p-10 text-center shadow-[0_8px_30px_rgba(15,23,42,0.06)]">
      <h2 class="text-xl sm:text-2xl font-semibold text-[color:var(--foreground)] mb-2">Still have questions?</h2>
      <p class="text-[color:var(--muted-foreground)] mb-6">Can't find the answer you're looking for? Our support team is here to help.</p>
      <a
        class="inline-flex items-center justify-center gap-2 rounded-xl bg-[color:var(--primary)] px-6 py-3 text-sm font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)]"
        href="mailto:support@nibni.com"
      >
        <lucide-icon name="mail" class="h-4 w-4"></lucide-icon>
        Contact Support
      </a>
    </div>
  </div>
</div>
